import { qoursesApi } from '@/api/qourses.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { Label } from '@/shadcn/components/ui/label.tsx'
import { Textarea } from '@/shadcn/components/ui/textarea.tsx'
import { ToastVariant, minDelay, sendGenericErrorNotification, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'

interface RegenerateOutreachModalProps {
  outreachId: string
  leadId: string
}

export default function RegenerateOutreachModal({
  outreachId,
  leadId,
}: RegenerateOutreachModalProps) {
  const [isLoading, setLoading] = useState(false)
  const [prompt, setPrompt] = useState('')
  const queryClient = useQueryClient()

  const handleRegenerateOutreach = async () => {
    if (!prompt.trim()) {
      sendNotification(
        'Validation Error',
        'Please provide a prompt for improving the outreach',
        ToastVariant.Error,
      )
      return
    }

    try {
      setLoading(true)
      await minDelay(
        qoursesApi.salesAdmin.salesAdminControllerRegenerateOutreachContentV1(outreachId, {
          prompt: prompt.trim(),
        }),
        1000,
      )

      // Invalidate outreach queries to refresh the data
      queryClient.invalidateQueries(['admin', 'outreach', leadId])

      sendNotification(
        'Outreach Regenerated',
        'The outreach content has been successfully regenerated',
        ToastVariant.Success,
      )
      popAllModals()
    } catch (e) {
      console.error(e)
      sendGenericErrorNotification()
      sendNotification(
        'Regeneration Failed',
        'The outreach could not be regenerated',
        ToastVariant.Error,
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dynamic.Content className="p-6 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogTitle className="mt-6 sm:mt-2">Regenerate Outreach Content</DialogTitle>
        <DialogDescription>
          Provide feedback or instructions to improve the outreach content using AI
        </DialogDescription>
      </DialogHeader>
      <div className="mb-8 space-y-6">
        <div className="grid w-full items-center gap-1.5">
          <Label htmlFor="prompt" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Improvement Prompt *
          </Label>
          <Textarea
            id="prompt"
            placeholder="e.g., Make it more friendly and personal, add a call-to-action, mention specific benefits..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isLoading}
            rows={4}
            className="text-sm sm:text-md"
          />
          <p className="text-xs text-gray-500">
            Describe how you want the AI to improve the outreach content. Be specific about tone,
            style, or content changes.
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={popAllModals} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleRegenerateOutreach}
          disabled={isLoading || !prompt.trim()}
          variant="indigo"
          className="overflow-hidden"
        >
          {isLoading ? (
            <>
              <motion.div
                initial={{
                  rotate: 45,
                }}
                animate={{
                  rotate: 45,
                  x: [-200, 200],
                  opacity: [0.4, 0.8, 0.4],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  repeatDelay: 0.5,
                }}
                className="z-100 absolute h-40 w-20 bg-white/0 bg-linear-to-r from-white/0 via-white/30 to-white/0"
              />
              <span className="mr-2"></span>
              🤔💭 Thinking hard...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Regenerate Outreach
            </>
          )}
        </Button>
      </div>
    </Dynamic.Content>
  )
}
